import { GatsbyImage } from 'gatsby-plugin-image';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import {
  HtmlContentLoader, Icon, Attribution, Link, Slider, WysiwygStyleWrapper,
} from '@powdr/components';
import {
  BlockTypes, CarouselControlPlacement, NavigationDirection, ThemeProps as Prop,
} from '@powdr/constants';
import { ContentBlockType } from '@powdr/model';
import { themeUtil, clamp } from '@powdr/utils';

const CPNT = BlockTypes.BASIC_ALT;

// styled components
const ContentBlockBasicAlt = styled(Link)`
  .basic-alt-container {
    position: relative;
    margin-bottom: 50px;

    .cb-title, .cb-copy, .button-wrapper, .image-wrapper {
      text-align: center;
    }

    .image-wrapper {
      margin-bottom: 20px;
    }
  }

  .cb-title, .cb-copy {
    margin-bottom: 20px;
  }

  .cb-title {}

  .cb-copy {
    ${() => clamp(6)}
    line-height: 1.35rem;
  }

  .button {
    display: inline-block;
  }

  &:hover .button, &:focus .button {
    color: ${({ $colorProfile }) => themeUtil(Prop.BTN_TXT_HOVER, CPNT, $colorProfile)};
    background-color: ${({ $colorProfile }) => themeUtil(Prop.BTN_BG_HOVER, CPNT, $colorProfile)};
  }

  &:focus .button {
    outline: 2px solid ${({ $colorProfile }) => themeUtil(Prop.BTN_BG_HOVER, CPNT, $colorProfile)};
    outline-offset: 2px;
  }

  .slider {
    margin-bottom: 20px;
  }
`;

const ImageContainer = styled.div`
  display: grid;
  margin-bottom: 20px;

  ${({ $icon, $image }) => (($icon && !$image) ? 'padding: 0 10%;' : '')}

  .cb-icon {
    max-height: 150px;
  }
`;

const IconAlignmentWrapper = styled.div`
  grid-area: 1/1;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;

  .video-icon {

    svg {
      fill: ${({ $colorProfile }) => themeUtil(Prop.ICON, CPNT, $colorProfile)};
      color: ${({ $colorProfile }) => themeUtil(Prop.ICON, CPNT, $colorProfile)};
    }

    &:hover {
      fill: ${({ $colorProfile }) => themeUtil(Prop.ICON_HOVER, CPNT, $colorProfile)};
      color: ${({ $colorProfile }) => themeUtil(Prop.ICON_HOVER, CPNT, $colorProfile)};
    }
  }
`;

const StyledImage = styled(GatsbyImage)`
  grid-area: 1/1;
`;

const Image = styled.img`
  grid-area: 1/1;
  width: 100%;
`;

export const BasicAlt = ({
  title, copy, blockType, colorProfile, image, isStaticImageLink, ariaLabel, iconName,
  buttonEnabled, buttonText, buttonLink, buttonTarget, buttonClick, formattedContent, inert,
  imageCarousel, videoId,
}) => (
  <ContentBlockBasicAlt
    href={buttonLink}
    target={buttonTarget}
    $colorProfile={colorProfile}
    onClick={() => buttonClick()}
    aria-label={ariaLabel}
    role="button"
    className={`content-block-${blockType} `}
    inert={inert}
  >
    <div className="basic-alt-container">
      {(imageCarousel || image || (!image && iconName)) && (
        (imageCarousel) ? (
          <Slider
            relationships={{ slides: imageCarousel }}
            parentColorProfile={colorProfile}
            isThumbnailsDisabled
            additionalControls={{
              controlSettings: {
                navigation: {
                  placement: CarouselControlPlacement.ON_SLIDE,
                  iconName: 'ui-arrow-left',
                  iconSize: 20,
                  classes: {
                    [NavigationDirection.PREVIOUS]: 'cb-control-previous',
                    [NavigationDirection.NEXT]: 'cb-control-next',
                  },
                },
              },
            }}
          />
        ) : (
          <ImageContainer $icon={!!(iconName)} $image={!!(image)}>
            {(!isStaticImageLink && image) && (
            <StyledImage
              image={image?.src}
              alt={image?.altText}
            />
            )}
            {(isStaticImageLink && image) && (
            <Image
              src={image?.src}
              alt={image?.altText}
            />
            )}
            {(!image && iconName) && <Icon className="cb-icon" name={iconName} />}
            {(videoId) && (
              <IconAlignmentWrapper $colorProfile={colorProfile}>
                <Icon className="video-icon" name="content-play" width="85" height="85" />
              </IconAlignmentWrapper>
            )}
            {(image?.attribution) && <Attribution>{image?.attribution}</Attribution>}
          </ImageContainer>
        )
      )}
      {(title) && <h3 className="cb-title">{title}</h3>}
      {(copy) && (<p className="cb-copy">{copy}</p>)}
      {(formattedContent) && <WysiwygStyleWrapper className="cb-formatted-content"><HtmlContentLoader html={formattedContent} allowRerender /></WysiwygStyleWrapper>}
      {(buttonText && buttonEnabled) && (
        <div className="button-wrapper">
          <div className="button with-background " aria-label={ariaLabel}>{buttonText}</div>
        </div>
      )}
    </div>
  </ContentBlockBasicAlt>
);

BasicAlt.propTypes = PropTypes.objectOf(ContentBlockType).isRequired;
